'use client';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { clsx } from 'clsx';
import { useMemo, useState } from 'react';

import styles from './DatePicker.module.scss';

import type { Dayjs } from 'dayjs';

export type DateRangeValue = {
  start: Dayjs | null;
  end: Dayjs | null;
};

type SharedProps = {
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  error?: string;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  className?: string;
  format?: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disablePast?: boolean;
  disableFuture?: boolean;
  closeOnSelect?: boolean;
};

type SingleDatePickerProps = SharedProps & {
  mode?: 'single';
  name?: string;
  value?: Dayjs | null;
  defaultValue?: Dayjs | null;
  onChange?: (value: Dayjs | null) => void;
};

type RangeDatePickerProps = SharedProps & {
  mode: 'range';
  startName?: string;
  endName?: string;
  startLabel?: string;
  endLabel?: string;
  value?: DateRangeValue;
  defaultValue?: DateRangeValue;
  onChange?: (value: DateRangeValue) => void;
  layout?: 'horizontal' | 'vertical';
};

export type DatePickerProps = SingleDatePickerProps | RangeDatePickerProps;

const EMPTY_RANGE: DateRangeValue = {
  start: null,
  end: null,
};

export function DatePicker(props: DatePickerProps) {
  if (props.mode === 'range') {
    return <RangeDatePickerImpl {...props} />;
  }

  return <SingleDatePickerImpl {...props} />;
}

function SingleDatePickerImpl({
  name,
  label,
  value,
  defaultValue,
  onChange,
  error,
  helperText,
  disabled = false,
  required = false,
  readOnly = false,
  size = 'small',
  fullWidth = true,
  className,
  format = 'YYYY-MM-DD',
  minDate,
  maxDate,
  disablePast,
  disableFuture,
  closeOnSelect = true,
}: SingleDatePickerProps) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<Dayjs | null>(
    value ?? defaultValue ?? null,
  );

  const actualValue = isControlled ? (value ?? null) : internalValue;

  const handleChange = (nextValue: Dayjs | null) => {
    if (!isControlled) {
      setInternalValue(nextValue);
    }

    onChange?.(nextValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className={clsx(styles.datePicker, className)}>
        <MuiDatePicker
          label={label}
          value={actualValue}
          onChange={handleChange}
          format={format}
          minDate={minDate}
          maxDate={maxDate}
          disablePast={disablePast}
          disableFuture={disableFuture}
          closeOnSelect={closeOnSelect}
          slotProps={{
            textField: {
              name,
              required,
              disabled,
              error: Boolean(error),
              helperText: error || helperText,
              size,
              fullWidth,
              InputProps: {
                readOnly,
              },
            } as any,
            popper: {
              className: 'nn-date-picker-popper',
            },
          }}
        />
      </div>
    </LocalizationProvider>
  );
}

function RangeDatePickerImpl({
  label,
  value,
  defaultValue,
  onChange,
  startName,
  endName,
  startLabel,
  endLabel,
  error,
  helperText,
  disabled = false,
  required = false,
  readOnly = false,
  size = 'small',
  fullWidth = true,
  className,
  format = 'YYYY-MM-DD',
  minDate,
  maxDate,
  disablePast,
  disableFuture,
  closeOnSelect = true,
  layout = 'horizontal',
}: RangeDatePickerProps) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<DateRangeValue>(
    value ?? defaultValue ?? EMPTY_RANGE,
  );

  const actualValue = isControlled ? (value ?? EMPTY_RANGE) : internalValue;
  const resolvedStartLabel = startLabel || (label ? `${label} start` : 'Start');
  const resolvedEndLabel = endLabel || (label ? `${label} end` : 'End');

  const rangeClassName = useMemo(
    () =>
      layout === 'vertical'
        ? styles.datePickerRangeVertical
        : styles.datePickerRangeHorizontal,
    [layout],
  );

  const setNextRange = (nextValue: DateRangeValue) => {
    if (!isControlled) {
      setInternalValue(nextValue);
    }

    onChange?.(nextValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className={clsx(styles.datePicker, className)}>
        <div className={clsx(styles.datePickerRange, rangeClassName)}>
          <MuiDatePicker
            label={resolvedStartLabel}
            value={actualValue.start}
            onChange={(nextStart) =>
              setNextRange({
                start: nextStart,
                end: actualValue.end,
              })
            }
            format={format}
            minDate={minDate}
            maxDate={maxDate}
            disablePast={disablePast}
            disableFuture={disableFuture}
            closeOnSelect={closeOnSelect}
            slotProps={{
              textField: {
                name: startName,
                required,
                disabled,
                error: Boolean(error),
                size,
                fullWidth,
                InputProps: {
                  readOnly,
                },
              } as any,
              popper: {
                className: 'nn-date-picker-popper',
              },
            }}
          />

          <MuiDatePicker
            label={resolvedEndLabel}
            value={actualValue.end}
            onChange={(nextEnd) =>
              setNextRange({
                start: actualValue.start,
                end: nextEnd,
              })
            }
            format={format}
            minDate={minDate}
            maxDate={maxDate}
            disablePast={disablePast}
            disableFuture={disableFuture}
            closeOnSelect={closeOnSelect}
            slotProps={{
              textField: {
                name: endName,
                required,
                disabled,
                error: Boolean(error),
                size,
                fullWidth,
                InputProps: {
                  readOnly,
                },
              } as any,
              popper: {
                className: 'nn-date-picker-popper',
              },
            }}
          />
        </div>

        {error || helperText ? (
          <p className={clsx(styles.helperText, error && styles.errorText)}>
            {error || helperText}
          </p>
        ) : null}
      </div>
    </LocalizationProvider>
  );
}
